/**
 * Justified-rows layout, the arrangement used by essentially every serious
 * photo gallery.
 *
 * Every row shares one height; widths vary with aspect ratio, so nothing is
 * ever cropped and both edges stay flush. A uniform grid would be simpler but
 * would centre-crop every portrait frame, which for a photographer's own work
 * is the wrong trade.
 */

export interface LayoutItem {
  id: string;
  /** Intrinsic dimensions, post-EXIF-rotation. */
  w: number;
  h: number;
}

export interface PlacedItem extends LayoutItem {
  width: number;
  height: number;
}

export interface LayoutRow {
  items: PlacedItem[];
  height: number;
}

export interface JustifyOptions {
  containerWidth: number;
  /** Preferred row height; actual rows land near it, never exactly on it. */
  targetHeight: number;
  gap: number;
  /**
   * How far the final row may be stretched to fill the width. A part-full last
   * row scaled up to meet the edge makes one photo enormous, so past this
   * factor it is left at its natural height instead.
   */
  maxLastRowStretch?: number;
}

const DEFAULT_LAST_ROW_STRETCH = 1.5;

export function justify(items: LayoutItem[], options: JustifyOptions): LayoutRow[] {
  const { containerWidth, targetHeight, gap } = options;
  const maxStretch = options.maxLastRowStretch ?? DEFAULT_LAST_ROW_STRETCH;

  if (items.length === 0 || containerWidth <= 0 || targetHeight <= 0) return [];

  const rows: LayoutRow[] = [];
  let current: LayoutItem[] = [];
  let aspectSum = 0;

  for (const item of items) {
    const aspect = aspectOf(item);
    current.push(item);
    aspectSum += aspect;

    // Width the row would occupy at the target height, gaps included.
    const naturalWidth = aspectSum * targetHeight + gap * (current.length - 1);

    if (naturalWidth >= containerWidth) {
      rows.push(scaleRow(current, aspectSum, containerWidth, gap));
      current = [];
      aspectSum = 0;
    }
  }

  if (current.length > 0) {
    const scaled = scaleRow(current, aspectSum, containerWidth, gap);

    rows.push(
      scaled.height <= targetHeight * maxStretch
        ? scaled
        : layoutAt(current, targetHeight, gap),
    );
  }

  return rows;
}

/** Solves for the height at which this row exactly fills the container. */
function scaleRow(
  items: LayoutItem[],
  aspectSum: number,
  containerWidth: number,
  gap: number,
): LayoutRow {
  const available = containerWidth - gap * (items.length - 1);
  const height = available / aspectSum;

  return layoutAt(items, height, gap, containerWidth);
}

function layoutAt(
  items: LayoutItem[],
  height: number,
  gap: number,
  fillWidth?: number,
): LayoutRow {
  const rounded = Math.max(1, Math.round(height));

  const placed: PlacedItem[] = items.map((item) => ({
    ...item,
    height: rounded,
    width: Math.max(1, Math.round(aspectOf(item) * rounded)),
  }));

  // Rounding each width independently leaves the row a pixel or two short of
  // the container, which shows up as a ragged right edge down the page. The
  // remainder goes onto the last item so the edge stays flush.
  if (fillWidth !== undefined && placed.length > 0) {
    const used = placed.reduce((sum, p) => sum + p.width, 0) + gap * (placed.length - 1);
    const last = placed[placed.length - 1] as PlacedItem;
    last.width = Math.max(1, last.width + (fillWidth - used));
  }

  return { items: placed, height: rounded };
}

/**
 * Guards against a zero or missing dimension reaching the division. The
 * manifest already withholds assets without dimensions, so this is a second
 * line of defence rather than the primary one.
 */
function aspectOf(item: LayoutItem): number {
  if (!Number.isFinite(item.w) || !Number.isFinite(item.h) || item.w <= 0 || item.h <= 0) {
    return 1;
  }
  return item.w / item.h;
}

/**
 * Which thumbnail to request for a rendered width. Picking by rendered size
 * rather than a fixed breakpoint is what makes the density control actually
 * save bandwidth instead of just changing the zoom.
 */
export function thumbnailFor(renderedWidth: number, devicePixelRatio = 1): 't400' | 't900' | 't1800' {
  const needed = renderedWidth * Math.min(devicePixelRatio, 2);

  if (needed <= 400) return 't400';
  if (needed <= 900) return 't900';
  return 't1800';
}
